import { RefinementCtx, z } from 'zod';

import { makeUnknownValuePlaceholderSchema } from './unknownValuePlaceholder';

export function makeProtobufEnumSchema<K extends string, V extends number, TFallback = V>(
  protobufEnum: {
    [k in K]: V;
  },
  fallbackValue: TFallback,
) {
  const enumValues = Object.values(protobufEnum);
  const enumSchema = z.number().transform((value, ctx) => {
    if (enumValues.includes(value)) {
      return value as V;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid protobuf enum value: ${value}`,
    });
    return z.NEVER;
  });
  return makeUnknownValuePlaceholderSchema(enumSchema, fallbackValue);
}

export function makeTypescriptEnumSchema<
  K extends string,
  V extends number | string,
  TFallback = V,
>(typescriptEnum: { [k in K]: V }, fallbackValue: TFallback) {
  const enumValues = Object.values(typescriptEnum);
  const enumSchema = z
    .number()
    .or(z.string())
    .transform((value, ctx) => {
      if (enumValues.includes(value)) {
        return value as V;
      }
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid typescript enum value: ${value}`,
      });
      return z.NEVER;
    });
  return makeUnknownValuePlaceholderSchema(enumSchema, fallbackValue);
}

export function makeExcludeValuesTransform<V, E extends number>(...excludeValues: E[]) {
  return (v: V, ctx: RefinementCtx) => {
    if (excludeValues.includes(v as any)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unexpected value ${v}`,
      });
      return z.NEVER;
    }
    return v as Exclude<V, E>;
  };
}
